import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRoutes } from '../../../../constants/apiRoutes';
import { getAxios } from '../../../../util/axios';
import { quickLinkTypes } from '../types/QuickLinkModels';
import type { QuickLinkModel, SaveQuickLinkInput } from '../types/QuickLinkModels';
import { quickLinksNavigationQueryKey, quickLinksQueryKey } from './useQuickLinksNavigationQuery';

function buildQuickLinkFormData(input: SaveQuickLinkInput) {
  const formData = new FormData();
  formData.append('DisplayName', input.displayName);
  formData.append('QuickLinkType', input.quickLinkType.toString());
  formData.append('ShowInNav', input.showInNav ? 'true' : 'false');

  if (input.category) {
    formData.append('Category', input.category);
  }

  if (input.url) {
    formData.append('Url', input.url);
  }

  if (input.fileName) {
    formData.append('FileName', input.fileName);
  }

  if (input.file) {
    formData.append('File', input.file);
  }

  return formData;
}

function useInvalidateQuickLinks() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: quickLinksQueryKey });
    void queryClient.invalidateQueries({ queryKey: quickLinksNavigationQueryKey });
  };
}

export function useCreateQuickLinkMutation() {
  const invalidateQuickLinks = useInvalidateQuickLinks();

  return useMutation({
    mutationFn: async (input: SaveQuickLinkInput) =>
      (
        await getAxios().post<number>(apiRoutes.quickLinks.create(), buildQuickLinkFormData(input), {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
      ).data,
    onSuccess: invalidateQuickLinks
  });
}

export function useUpdateQuickLinkMutation() {
  const invalidateQuickLinks = useInvalidateQuickLinks();

  return useMutation({
    mutationFn: async ({ quickLinkId, input }: { quickLinkId: number; input: SaveQuickLinkInput }) =>
      (
        await getAxios().put<number>(apiRoutes.quickLinks.update(quickLinkId), buildQuickLinkFormData(input), {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
      ).data,
    onSuccess: invalidateQuickLinks
  });
}

export function useDeleteQuickLinkMutation() {
  const invalidateQuickLinks = useInvalidateQuickLinks();

  return useMutation({
    mutationFn: async (quickLinkId: number) => {
      await getAxios().delete(apiRoutes.quickLinks.delete(quickLinkId));
    },
    onSuccess: invalidateQuickLinks
  });
}

export async function downloadQuickLink(quickLink: QuickLinkModel): Promise<void> {
  if (!quickLink.FileName) {
    return;
  }

  const { data } = await getAxios().get<Blob>(apiRoutes.quickLinks.download(quickLink.Id), {
    responseType: 'blob'
  });
  const url = URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = quickLink.FileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function openQuickLink(quickLink: QuickLinkModel): Promise<void> {
  if (quickLink.QuickLinkType === quickLinkTypes.uploadedFile) {
    await downloadQuickLink(quickLink);
    return;
  }

  const url = quickLink.ResolvedUrl ?? quickLink.Url;
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
